import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { generateAxiosInstanceInstitute } from '../hooks/useAuthorizedRequestForInstitute';
import { generateAxiosInstanceSection } from '../hooks/useAuthorizedRequestForSection';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage } from '../validation/validationError';
import { adminExamRequestFormValidation, adminSubjectBlockFormValidation } from '../validation/Validation';
import { Loader } from '../components/ModalsCommonFunctions';
import { ZodError } from 'zod';
import Swal from 'sweetalert2';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';

const BlockSectionSubjects = () => {
    const authorizedAxios = generateAxiosInstance()
    const authorizedAxiosSection = generateAxiosInstanceSection()
    const authorizedAxiosInstitute = generateAxiosInstanceInstitute()

    const initialFormState = {
        instituteMasterId: "",
        sectionMasterId: "",
        subjectMasterId: "",
        instituteSubjectMasterId: "",
        subjectName: ""
    }

    const [subjectData, setSubjectData] = useState(initialFormState);
    const [instituteList, setInstituteList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [subList, setSubList] = useState([]);
    const [blockedSubjectList, setBlockedSubjectList] = useState([]);
    const [error, setError] = useState([])

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [subjectNameSort, setSubjectNameSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["subjectName"];


    const [loading, setLoading] = useState(false)

    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getInstituteList();
    }, []);

    const getApiUrl = () => {
        return `/get-section-subject-blocking?instituteMasterId=${subjectData.instituteMasterId}&sectionMasterId=${subjectData.sectionMasterId}`;
    }

    useEffect(() => {
        if (subjectData.instituteMasterId && subjectData.sectionMasterId) {
            getBlockedSubjectList();
        }
    }, [subjectData]);

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getBlockedSubjectList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setBlockedSubjectList(await searchArray("A", getApiUrl(), searchKeys, searchValue.trim()))
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(blockedSubjectList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(blockedSubjectList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > blockedSubjectList.length) {
            endIndex = blockedSubjectList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(blockedSubjectList.slice(startIndex, endIndex));
    }, [blockedSubjectList, currPage, itemsPerPage]);

    const getBlockedSubjectList = async () => {
        try {
            const response = await authorizedAxios.get(getApiUrl());
            setBlockedSubjectList(response.data.result);
        } catch (error) {
            setBlockedSubjectList([]);
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.error('Error fetching data:', errorMsg);
        }
    }

    const getInstituteList = () => {
        const apiUrlForInstituteList = `/get-institute-master`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstituteList)
            .then(response => {
                setInstituteList(response.data.result);
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            })
    }

    const handleInsituteChange = (e) => {
        const { name, value } = e.target;
        setSubjectData(prevData => {
            return {
                ...prevData,
                [name]: value,
                sectionMasterId: ''
            }
        })

        getSectionList(value);
        getSubject(value);
    }

    const getSectionList = (instituteMasterId) => {
        const apiUrlForSection = `/get-section?instituteMasterId=${instituteMasterId}`;
        authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrlForSection)
            .then(response => {
                setSectionList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const getSubject = (instituteMasterId) => {
        const apiUrl = `/get-institute-subjects?instituteMasterId=${instituteMasterId}`;
        authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrl)
            .then(response => {
                setSubList(response.data.result);
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSubjectData(prevData => {
            return {
                ...prevData,
                [name]: value
            }
        })

        if (value.trim().length !== 0) {
            setError(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const handleSubjectChange = (e) => {
        const { name, value } = e.target;
        const selectedSubject = subList.find(sub => sub.id == value);

        setSubjectData(prev => ({
            ...prev,
            [name]: value,
            subjectMasterId: selectedSubject.subjectMasterId,
            subjectName: selectedSubject.subjectName,
        }))

        if (value.trim().length !== 0) {
            setError(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const blockSubject = async () => {
        try {
            await adminSubjectBlockFormValidation.parseAsync(subjectData);
            if (!subjectData.subjectMasterId) {
                return Swal.fire({ text: "SubjectMasterId not found, Please contact with developer" });
            }
            if (!subjectData.subjectName) {
                return Swal.fire({ text: "Subject Name not found, Please contact with developer" });
            }
            setError([]);
            setLoading(true);
            const apiUrl = "/add-section-subject-blocking";
            const response = await authorizedAxios.post(apiUrl, subjectData);
            await getBlockedSubjectList();
            setSubjectData(prevData => ({
                ...prevData,
                subjectMasterId: "",
                instituteSubjectMasterId: "",
                subjectName: ""
            }))

            setSaveId(response.data.data.id);

            Swal.fire({ text: response.data.message });
        } catch (error) {
            // setBlockedSubjectList([]);
            if (error instanceof ZodError) {
                setError(error.errors);
            } else {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const deleteBlockFromSubject = async (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            try {
                setLoading(true);
                const apiUrl = `/delete-section-subject-blocking?id=${id}`;
                const response = await authorizedAxios.delete(apiUrl);
                const page = deleteItem(currPage, itemsPerPage, blockedSubjectList.length);
                await getBlockedSubjectList();
                setCurrPage(page);

                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        }
    }

    const handleInputReset = () => {
        setSubjectData(initialFormState);
        setBlockedSubjectList([]);
        setSectionList([]);
        setSubList([]);
        setError([]);
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <Header />

                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Block Subjects</span></li>
                        </ol>
                    </nav>
                </div>

                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa fa-ban"></i>Block Subjects</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="instituteMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Institute</label>
                                                    <select class="form-select" aria-label="Default select example" name='instituteMasterId' onChange={handleInsituteChange} value={subjectData.instituteMasterId} >
                                                        <option selected value={''} disabled>Select Institute</option>
                                                        {
                                                            instituteList?.map((institute) => {
                                                                return <option value={institute.id}>{institute.instituteName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    <ErrorMessage errors={error} field="instituteMasterId" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="sectionMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Section</label>
                                                    <select class="form-select" aria-label="Default select example" name='sectionMasterId' onChange={handleInputChange} value={subjectData.sectionMasterId} >
                                                        <option selected value={''} disabled>Select Section</option>
                                                        {
                                                            sectionList?.map((section) => {
                                                                return <option value={section.id}>{section.sectionName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    <ErrorMessage errors={error} field="sectionMasterId" />
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="instituteSubjectMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Subject</label>
                                                    <select class="form-select" aria-label="Default select example" name='instituteSubjectMasterId' onChange={handleSubjectChange} value={subjectData.instituteSubjectMasterId} >
                                                        <option selected value={''} disabled>Select Subject</option>
                                                        {
                                                            subList?.map((sub) => {
                                                                return <option value={sub.id}>{sub.subjectName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    <ErrorMessage errors={error} field="instituteSubjectMasterId" />
                                                </div>

                                                <div className="col-md-12 mt-3 d-flex justify-content-center button-container">
                                                    <button className="btn btn-primary" type="button" onClick={blockSubject}>Block</button>
                                                    <button className="btn btn-danger delete_icon" type="button" onClick={handleInputReset}>Reset</button>
                                                </div>

                                                {subjectData.instituteMasterId && subjectData.sectionMasterId && <div className="d-grid gap-2 mt-2">

                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">

                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope="col">
                                                                            Subject Name
                                                                            <SortIcon sortState={subjectNameSort} setSortState={setSubjectNameSort} setData={setBlockedSubjectList} setCurrPage={setCurrPage} sortingPath={"subjectName"} />
                                                                        </th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((sub, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                        <td>{sub?.subjectName}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" /*disabled={editId === course.id}*/ onClick={() => deleteBlockFromSubject(sub.id)}> <i class="fa-light fa-trash-can"></i></button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Subject Available</th>
                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>

                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={blockedSubjectList.length} />

                                                        </div>
                                                    </div>
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default BlockSectionSubjects
